import Vue from 'vue';

import _ from 'lodash';

import { getFields } from '_/components/Dashboard/Widgets/WeeklyChartWidget/WeeklyChartWidget.fields.js';

import { mapGetters, mapActions } from 'vuex';

import { userHasRole } from '_/vuex/actions/userActions';

export default {
    /**
     * Define props
     *
     * @type  {Object}
     */
    props: {
        widget: {
            required: true,
            type: Object,
        },
    },

    /**
     * Set the component data
     *
     * @return  {object}
     */
    data() {
        return {
            /**
             * Stores widget setup fields
             *
             * @type  {Array}
             */
            fields: [],

            /**
             * Is polling active?
             *
             * @type  {boolean}
             */
            polling: false,

            /**
             * Toggles the widget setup mode
             *
             * @type  {boolean}
             */
            setupMode: _.isEmpty(this.widget.module),

            /**
             * Source module data
             *
             * @type  {Object}
             */
            sourceModule: {
                /**
                 * Souce module fields
                 *
                 * @type  {Array}
                 */
                fields: [],
            },

            /**
             * An object sent to child components; used to signal that the fields need to be reset, with the ability
             * to limit the reset to only certain fields
             *
             * @type  {Object}
             */
            formFieldsReset: {
                /**
                 * A list of field names to include in a reset action
                 *
                 * @type  {Array}
                 */
                includeFields: [],

                /**
                 * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
                 * set the fresh value.
                 *
                 * @type  {integer}
                 */
                resetData: null,
            },
        };
    },

    /**
     * Set the computed properties
     *
     * @type {object}
     */
    computed: {
        ...mapGetters({
            photonModule: 'photonModule/photonModule',
            ui: 'ui/ui',
        }),

        /**
         * Sets the widget theme CSS class
         *
         * @return  {string}
         */
        getWidgetTheme() {
            return this.widget.theme ? this.widget.theme : 'panel-primary';
        },

        /**
         * prepares the module dropdown data
         *
         * @return  {array}
         */
        moduleOptions() {
            return this.photonModule.modules.map((module) => {
                return {
                    id: module.table_name,
                    text: module.name,
                };
            });
        },

        /**
         * Unpacks the meta_data JSON to object
         *
         * @return  {object}
         */
        metaData() {
            return this.widget.meta_data;
        },
    },

    /**
     * Set the methods
     *
     * @type  {Object}
     */
    methods: {
        ...mapActions('widget', [
            'deleteEntry',
            'getWeeklyChartData',
            'getSelectedModuleFields',
            'submitEntry',
            'updateWidget',
        ]),

        /**
         * Toggles the setup mode
         *
         * @return  {void}
         */
        toggleSetup() {
            if (this.polling) {
                clearTimeout(this.polling);
            }

            if (this.animationTimeout) {
                clearTimeout(this.animationTimeout);
            }

            if (this.setupMode) {
                let payload = {
                    ...this.widget,
                };

                this.submitEntry({ payload }).then((response) => {
                    this.updateWidget({ widget: response.entry }).then(() => {
                        this.setupMode = false;

                        setTimeout(() => {
                            this.getModuleData(true);
                        }, 300);
                    });
                });
            } else {
                this.setupMode = true;
            }
        },

        /**
         * Removes the widget
         *
         * @return  {void}
         */
        removeWidget() {
            this.deleteEntry({ entryId: this.widget.id }).then(() => {
                if (this.polling) {
                    clearTimeout(this.polling);
                }
            });
        },

        /**
         * Gets the latest module entries
         *
         * @param   {boolean}  longpoll
         * @return  {void}
         */
        getModuleData(longpoll) {
            if (!this.widget.module) {
                this.sourceModule = {};

                return;
            }

            this.getWeeklyChartData({
                moduleTableName: this.widget.module,
                chartPeriod: this.metaData.chart_period,
            }).then((data) => {
                Morris.Bar({
                    element: 'hero-bar',
                    data,
                    xkey: 'day',
                    ykeys: ['count'],
                    labels: ['Total items'],
                    barRatio: 0.1,
                    xLabelAngle: 90,
                    hideHover: 'auto',
                });

                const refreshInterval = parseInt(this.widget.refresh_interval, 10);

                if (refreshInterval && longpoll) {
                    this.polling = setTimeout(() => {
                        this.getModuleData(longpoll);
                    }, refreshInterval);
                }
            });
        },

        /**
         * Checks if a user has given role
         *
         * @param   {string}  role
         * @return  {bool}
         */
        userHasRole,
    },

    /**
     * Set the mounted hook
     *
     * @return  {void}
     */
    mounted() {
        this.$nextTick(() => {
            this.getSelectedModuleFields({ moduleTableName: this.widget.module }).then((fields) => {
                this.sourceModule.fields = fields;

                this.fields = getFields(this);

                const longpoll = !this.widget.setupMode;

                this.getModuleData(longpoll);
            });
        });
    },

    /**
     * Define watched properties
     *
     * @type  {Object}
     */
    watch: {
        widget: {
            deep: true,
            handler(newEntry) {
                this.getSelectedModuleFields({ moduleTableName: newEntry.module }).then((fields) => {
                    Vue.set(this.sourceModule, 'fields', fields);

                    this.fields = getFields(this);

                    this.formFieldsReset.resetData = moment().valueOf();
                });
            },
        },
    },

    /**
     * Set the beforeDestroy hook
     *
     * @return  {void}
     */
    beforeDestroy() {
        if (this.polling) {
            clearTimeout(this.polling);
        }
    },
};
